import React, { useEffect, useState } from "react";
import {
	Box,
	Typography,
	useTheme,
	Button as MUIButton,
	Divider,
} from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setUser } from "../../../store/features/authSlice";
import { useNavigate } from "react-router-dom";
import { base_URL } from "../../../config/config";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import OutlinedLabelledTextField from "../../../components/OutlinedLabelledTextfield";
import { Facebook, Apple, Google } from "../../../svgs";
import User from "../../../assets/img/user.png";

const LoginModal = ({ isOpen, onClose }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState({ email: "", password: "", success: "" });

	useEffect(() => {
		window.fbAsyncInit = function () {
			window.FB.init({
				appId: process.env.REACT_APP_FACEBOOK_APP_ID,
				cookie: true,
				xfbml: true,
				version: "v17.0",
			});
		};

		(function (d, s, id) {
			let js,
				fjs = d.getElementsByTagName(s)[0];
			if (d.getElementById(id)) return;
			js = d.createElement(s);
			js.id = id;
			js.src = "https://connect.facebook.net/en_US/sdk.js";
			fjs.parentNode.insertBefore(js, fjs);
		})(document, "script", "facebook-jssdk");
	}, []);

	if (!isOpen) return null; // close modal

	const validate = () => {
		let valid = true;
		if (!email) {
			setError((p) => ({ ...p, email: "Please enter your email address." }));
			valid = false;
		}
		if (!password) {
			setError((p) => ({ ...p, password: "Please enter your password." }));
			valid = false;
		}
		return valid;
	};

	const handleLogin = async () => {
		if (!validate()) return;

		setIsLoading(true);

		try {
			const response = await axios.post(`${base_URL}/api/auth/login`, {
				email,
				password,
			});

			const { token, user } = response.data;

			if (user.isVerified) {
				toast.success("Logged in successfully!");

				dispatch(setUser(user));
				localStorage.setItem("authToken", token);
				localStorage.setItem("authUser", JSON.stringify(user));

				setIsLoading(false);
				onClose();
			} else {
				setIsLoading(false);
				toast.error("Please verify your email to login.");
			}
		} catch (err) {
			setIsLoading(false);
			setError((p) => ({
				...p,
				success: "Login failed. Please check your credentials.",
			}));
		}
	};

	const handleFacebookResponse = async (response) => {
		try {
			if (response.accessToken) {
				const result = await axios.get(
					`${base_URL}/api/auth/facebook?access_token=${response.accessToken}`
				);

				const { data, token } = result.data;

				dispatch(setUser(data));
				localStorage.setItem("authToken", token);
				localStorage.setItem("authUser", JSON.stringify(data));

				toast.success("Logged in successfully!");
				onClose();
			}
		} catch (error) {
			toast.error("Facebook login failed");
		}
	};

	const isFormValid = () => email.trim() !== "" && password.trim() !== "";

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
			<div className="bg-white rounded-xl w-[92%] max-w-md p-6 shadow-xl relative animate-fade-in">
				{/* Close Button */}
				<button
					onClick={onClose}
					className="absolute top-3 right-3 text-gray-600 text-2xl font-bold"
				>
					✕
				</button>

				{/* HEADER */}
				<div className="flex items-center justify-center space-x-3 mb-6">
					<img src={User} alt="User" className="h-10" />
					<h2 className="text-2xl font-bold">User Login</h2>
				</div>

				{/* FORM */}
				<div className="flex flex-col gap-4">
					<OutlinedLabelledTextField
						label="Email address"
						type="text"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						error={error.email}
					/>

					<OutlinedLabelledTextField
						label="Password"
						type="password"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						error={error.password}
					/>

					{/* Error */}
					{error.success && (
						<p className="text-red-600 text-sm">{error.success}</p>
					)}

					{/* Login Button */}
					<MUIButton
						onClick={handleLogin}
						variant="contained"
						fullWidth
						size="large"
						sx={{
							borderRadius: "8px",
							mt: 1,
							backgroundColor: isFormValid() ? "primary.main" : "grey.border",
							color: isFormValid() ? "white" : "text",
						}}
						disabled={isLoading || !isFormValid()}
					>
						{isLoading ? "Logging in..." : "Log in"}
					</MUIButton>
				</div>

				{/* SOCIAL LOGIN */}
				<div className="flex justify-center gap-4 mt-6">
					<FacebookLogin
						appId={process.env.REACT_APP_FACEBOOK_APP_ID}
						autoLoad={false}
						fields="name,email,picture"
						callback={handleFacebookResponse}
						render={(renderProps) => (
							<div
								onClick={renderProps.onClick}
								className="border border-gray-400 rounded-md px-4 py-2 cursor-pointer flex justify-center items-center"
							>
								<Facebook />
							</div>
						)}
					/>

					<div className="border border-gray-400 rounded-md px-4 py-2 flex items-center">
						<Apple />
					</div>

					<div className="border border-gray-400 rounded-md px-4 py-2 cursor-pointer flex items-center">
						<Google />
					</div>
				</div>

				<Divider className="my-4" />

				{/* Sign Up */}
				<div className="text-center">
					<p className="text-gray-600">
						Don’t have an account?{" "}
						<span
							onClick={() => navigate("/user-signup")}
							className="text-blue-600 underline cursor-pointer font-medium"
						>
							Sign up
						</span>
					</p>
				</div>
			</div>

			{/* Modal animation */}
			<style>{`
                @keyframes fade-in {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-fade-in {
                    animation: fade-in 0.25s ease-out;
                }
            `}</style>
		</div>
	);
};

export default LoginModal;
